import type { HTMLAttributes, ReactNode } from 'react'
import { cn } from '../lib/cn'

/**
 * Etapas em linha, cada uma com nome, descrição, a contagem do que já tem e as ações para
 * acrescentar; abaixo, o que a etapa guarda (as miniaturas). É a forma das quatro etapas de foto
 * de um plano, e serve a qualquer sequência curta com contagem. Contagem zerada é pendência quando
 * `countPending` diz: o número fica em plum. Abaixo de 900px cada etapa é um cartão com as ações
 * sempre à vista.
 */
export interface StageItemProps extends Omit<HTMLAttributes<HTMLElement>, 'title'> {
  title: ReactNode
  description?: ReactNode
  /** A contagem por extenso ("2 fotos", "nenhuma foto"). */
  count: ReactNode
  /** A contagem é uma pendência: fica em plum. */
  countPending?: boolean
  /** As ações da etapa, em texto ou botão pequeno. */
  actions?: ReactNode
  /** O que a etapa já guarda, abaixo do cabeçalho (uma `PhotoGrid`). */
  children?: ReactNode
}

export function StageItem({
  title,
  description,
  count,
  countPending,
  actions,
  children,
  className,
  ...props
}: StageItemProps) {
  return (
    <section
      data-slot="stage-item"
      data-pending={countPending || undefined}
      className={cn(
        'border-hairline flex min-w-0 flex-col gap-3 border-b px-4 py-4 last:border-b-0',
        className,
      )}
      {...props}
    >
      <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
        <div className="min-w-0">
          <h3 className="font-display text-lead text-ink leading-[1.3] font-normal">{title}</h3>
          {description ? (
            <p className="font-body text-secondary mt-1 text-[12px] leading-[1.5] font-light">
              {description}
            </p>
          ) : null}
          <p
            data-slot="stage-count"
            className={cn(
              'font-body mt-1 text-[12px] leading-[1.5] font-light',
              countPending ? 'text-plum' : 'text-label',
            )}
          >
            {count}
          </p>
        </div>
        {actions ? (
          <div className="font-body text-eyebrow text-label max-desk:w-full flex shrink-0 flex-wrap items-center gap-x-4 gap-y-2 font-light tracking-normal">
            {actions}
          </div>
        ) : null}
      </div>
      {children}
    </section>
  )
}

/** O contêiner das etapas: superfície branca com fio; cada etapa separada por fio. */
export function StageList({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      data-slot="stage-list"
      className={cn('border-hairline bg-surface border', className)}
      {...props}
    />
  )
}
